import { Button } from "../Components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify"; // Import toast and ToastContainer
import "react-toastify/dist/ReactToastify.css"; // Import toastify CSS
import { RootState } from "../State/store";

// Mock Card components
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white border rounded-lg shadow-sm ${className}`}>{children}</div>
);
const CardContent = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`${className}`}>{children}</div>
);

const StatusBox = ({ label, status }: { label: string; status: string }) => {
  const isApproved = status === "approved";
  const hideStatusText = label === "Start" || label === "End" || label === "Campaign Creation";
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate("/operatorNavbar/campaignlist");
  };

  return (
    <div className="flex flex-col items-center min-w-[140px]">
      <div className="fixed flex justify-end items-center right-0 top-[-15px] z-20 p-4">
        <Button
          onClick={handleBackClick}
          className="w-30 text-sm text-[#F8FAFC] font-medium h-[35px] mt-[10px] cursor-pointer"
        >
          Back
        </Button>
      </div>
      <div
        className={`rounded-md px-6 py-6 text-sm font-semibold w-full text-center ${
          isApproved ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-600"
        }`}
      >
        {label}
      </div>
      <div
        className={`text-xs mt-2 ${
          isApproved ? "text-gray-600" : "text-gray-400"
        } ${hideStatusText ? "invisible" : ""}`}
      >
        {isApproved ? "Approved" : "Pending"}
      </div>
    </div>
  );
};

export default function ApprovalTimeline() {
  const location = useLocation();
  const navigate = useNavigate();
  interface LocationState {
    campaignId?: number;
  }
  const { campaignId } = location.state as LocationState || {};
  const campaign_id = campaignId; // Map campaignId from navigation state

  const apiUrlAdvAcc = useSelector((state: RootState) => state.authentication.apiURL);
  const [steps, setSteps] = useState<{ label: string; status: string }[]>([]);
  const [verticalTimeline, setVerticalTimeline] = useState<any[]>([]);
  const OperatorUrl = useSelector((state: RootState) => state.authentication.operatorUrl);

useEffect(() => {
  if (!campaign_id) {
    toast.error("No campaign ID provided. Redirecting to campaign list.");
    navigate("/navbar/campaignlist");
    return;
  }

  if (!OperatorUrl || !apiUrlAdvAcc) {
    toast.error("API configuration missing. Please try again later.");
    navigate("/navbar/campaignlist");
    return;
  }

  const GetHorizontalTimelineStatus = async () => {
    try {
      const response = await axios.get(`${apiUrlAdvAcc}/GetHorizontalTimelineStatus`, {
        params: { campaign_id },
      });

      if (response.data.status === "Success") {
        const apiSteps = response.data.data.map((item: any) => ({
          label: item.stage,
          status: item.status.toLowerCase(),
        }));
        setSteps(apiSteps);
      } else {
        toast.warn("No timeline status found for this campaign.");
      }
    } catch (error) {
      console.error("Error fetching horizontal timeline status:", error);
      toast.error("Failed to fetch timeline status.");
    }
  };

const GetVerticalTimeline = async () => {
  try {
    const response = await axios.get(`${OperatorUrl}/GetOperatorVerticalTimeline`, {
      params: { campaign_id },
    });

    if (response.data.status === "Success") {
      const timeline = response.data.data;

      const hasOperatorStep = timeline.some((item: any) =>
        item.title.toLowerCase().includes("operator approved") ||
        item.title.toLowerCase().includes("operator rejected")
      );

      if (!hasOperatorStep) {
        const adminIndex = timeline.findIndex((item: any) =>
          item.title.toLowerCase().includes("admin approved")
        );

        if (adminIndex !== -1) {
          // ✅ Check if there’s no valid next stage after admin
          const hasNextStage = timeline[adminIndex + 1] && timeline[adminIndex + 1].status !== "pending";

          if (!hasNextStage) {
            timeline.splice(adminIndex + 1, 0, {
              title: "Operator Approval",
              time: "Pending",
              status: "pending",
              comment: null,
            });
          }
        }
      }

      setVerticalTimeline(timeline);
    } else {
      toast.warn("No vertical timeline data available.");
    }
  } catch (error) {
    console.error("Error fetching vertical timeline:", error);
    toast.error("Failed to fetch vertical timeline.");
  }
};



  GetHorizontalTimelineStatus();
  GetVerticalTimeline();
}, [campaign_id, OperatorUrl, apiUrlAdvAcc, navigate]);

  return (
    <div className="p-8 bg-white min-h-screen">
      <ToastContainer /> {/* Add ToastContainer for notifications */}
      {/* Horizontal Status Bar */}
      <div className="flex items-center justify-center w-full max-w-6xl mx-auto mb-16">
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <StatusBox label={step.label} status={step.status} />
            {index < steps.length - 1 && (
              <div
                className={`h-1 w-10 mt-4 mb-8 top-[-10px] ${
                  step.status === "approved" && steps[index + 1].status === "approved"
                    ? "bg-blue-600"
                    : "bg-gray-300"
                }`}
              ></div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Vertical Timeline */}
      <div className="flex justify-center mb-16">
        <div className="relative max-w-4xl w-full">
          <div className="relative z-10">
            {verticalTimeline.map((event: any, index: number) => {
              const isLeftSide = index % 2 === 0;
              const isPending = event.status?.toLowerCase() === "pending";
              const dotColor = isPending ? "bg-gray-400" : "bg-blue-600";
              const textColor = isPending ? "text-gray-400" : "text-blue-700";
              const timeColor = isPending ? "text-gray-300" : "text-gray-500";

              return (
                <div key={index} className="relative py-8 pb-12 flex items-start">
                  {index < verticalTimeline.length - 1 && (
                    <div className="absolute left-1/2 top-3 h-full w-0.5 bg-blue-600 transform -translate-x-1/2 z-0" />
                  )}
                  <div
                    className={`absolute left-1/2 top-3 w-3 h-3 rounded-full ${dotColor} transform -translate-x-1/2 z-10`}
                  />

                  {/* Left Side */}
                  <div className="w-1/2 flex justify-end pr-4">
                    <div className="w-72 -mt-6">
                      {isLeftSide ? (
                        <div className="text-right">
                          <div className={`text-sm font-semibold ${textColor}`}>{event.title}</div>
                          {event.time && <div className={`text-xs ${timeColor} mt-1`}>{event.time}</div>}
                        </div>
                      ) : (
                        <div className="flex justify-end">
                          {event.comment && (
                            <Card
                              className={`${isPending ? "border-gray-200 bg-gray-50" : "border-blue-200 bg-blue-50"} max-w-xs`}
                            >
                              <CardContent className="p-4">
                                <div className={`text-sm font-semibold ${textColor} mb-2`}>{event.comment.title}</div>
                                <div
                                  className={`text-sm break-words whitespace-pre-wrap ${
                                    isPending ? "text-gray-400" : "text-blue-600"
                                  }`}
                                >
                                  {event.comment.content}
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Side */}
                  <div className="w-1/2 flex justify-start pl-4">
                    <div className="w-72 -mt-6">
                      {!isLeftSide ? (
                        <div className="text-left">
                          <div className={`text-sm font-semibold ${textColor}`}>{event.title}</div>
                          {event.time && <div className={`text-xs ${timeColor} mt-1`}>{event.time}</div>}
                        </div>
                      ) : (
                        <div className="flex justify-start">
                          {event.comment && (
                            <Card
                              className={`${isPending ? "border-gray-200 bg-gray-50" : "border-blue-200 bg-blue-50"} max-w-xs`}
                            >
                              <CardContent className="p-4">
                                <div className={`text-sm font-semibold ${textColor} mb-2`}>{event.comment.title}</div>
                                <div
                                  className={`text-sm break-words whitespace-pre-wrap ${
                                    isPending ? "text-gray-400" : "text-blue-600"
                                  }`}
                                >
                                  {event.comment.content}
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}